/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description Constants for era
 */

import _ from 'lodash';
import { MONTH_DATE_YEAR_SLASH_FORMAT, SORT_COLUMN_LABELS } from '@veritable/common/constants';

// era filter fields
const ERA_FILTER_FIELDS = {
	paymentMethod: {
		label: 'Payment Method',
		placeholder: 'Enter the Payment Method',
	},
	payerName: {
		label: 'Payer',
		placeholder: 'Select a Payer',
	},
	paymentDate: {
		label: 'Payment Date',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
	},
	reconciledStatus: {
		label: 'Reconciled Status',
		placeholder: 'Select the Reconciled State',
	},
	amount: {
		label: 'Payment Received',
		placeholder: 'E.g.: 500',
	},
	claimNumber: {
		label: 'Claim ID',
		placeholder: 'E.g.: 2223456E7390',
	},
	eraNumber: {
		label: 'ERA No.',
		placeholder: 'E.g.: 2223456E7390',
	},
};

// era claim fields
const ERA_CLAIM_FILTER_FIELDS = {
	claimNumber: {
		label: 'Claim ID',
		placeholder: 'Enter the Claim ID',
	},
	claimFrequency: {
		label: 'Claim Frequency',
		placeholder: 'Select Claim Frequency',
	},
	patientName: {
		label: 'Patient Name',
		placeholder: 'Enter the Patient Name',
	},
	patientId: {
		label: 'Patient ID',
		placeholder: 'Enter the Patient ID',
	},
	billed: {
		label: 'Billed',
		placeholder: 'Enter the Billed Amount',
	},
};

// payment reconciliation status
const PAYMENT_RECONCILIATION_STATUS: Record<string, Fhir.PaymentReconciliationStatus> = {
	active: 'active',
	cancelled: 'cancelled',
	draft: 'draft',
	enteredInError: 'entered-in-error',
};

// payment reconciliation outcome
const PAYMENT_RECONCILIATION_OUTCOME: Record<string, Fhir.PaymentReconciliationOutcome> = {
	queued: 'queued',
	complete: 'complete',
	error: 'error',
	partial: 'partial',
};

// reconciled status options
const RECONCILED_STATUS_OPTIONS: { label: string; value: Fhir.PaymentReconciliationOutcome }[] = [
	{
		label: 'Reconciled',
		value: 'complete',
	},
	{
		label: 'Not Reconciled',
		value: 'queued',
	},
];

// era state
const ERA_STATE: Record<string, ERA.ERAState> = {
	open: 'open',
	resolved: 'resolved',
};

// post payment batch status
const POST_PAYMENT_BATCH_STATUS: Record<string, PatientPayment.IPostPaymentBatchStatus> = {
	inProgress: 1,
	completed: 2,
	scheduled: 3,
	partialCompleted: 4,
	draft: 0,
	canceled: -1,
	abandoned: -2,
	timeout: -3,
};

// post payment claim processing status

const POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS: Record<string, PatientPayment.IPostPaymentBatchClaimProcessingStatus> = {
	invalid: -3,
	cancelled: -2,
	failed: -1,
	pending: 0,
	success: 1,
};

const POST_PAYMENT_GROUPED_STATUS: Record<
	PatientPayment.IPostPaymentGroupedStatus,
	{
		value: PatientPayment.IPostPaymentGroupedStatus;
		label: string;
		groupedValue: PatientPayment.IPostPaymentBatchClaimProcessingStatus[];
	}
> = {
	success: { value: 'success', label: 'Success', groupedValue: [POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS.success] },
	skipped: {
		value: 'skipped',
		label: 'Skipped',
		groupedValue: [
			POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS.invalid,
			POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS.cancelled,
			POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS.failed,
		],
	},
};

// ERA list view sort columns
const ERA_TABLE_SORT_COLUMNS: Record<string, ERA.ERASortColumns> = {
	chargeAmount: 'chargeAmount',
	actualPaymentAmount: 'actualPaymentAmount',
};

// ERA list view sort options, no need of charge amount filter for now
const ERA_TABLE_SORT_OPTIONS: SortItemType[] = _.map(
	[
		{
			apikey: ERA_TABLE_SORT_COLUMNS.actualPaymentAmount,
			asc: false,
			label: `Payment Received (${SORT_COLUMN_LABELS.highToLow})`,
		},
	],
	(option) => ({ ...option, key: option.label }),
);

// ERA detail page - claims table sort columns
const ERA_CLAIM_TABLE_SORT_COLUMNS: Record<string, ClaimStatusRequests.ISortColumn> = {
	ClaimAge: 'claimAge',
	ClaimAmount: 'claimAmount',
	PatientResponsibility: 'patientResponsibility',
	PendingPatientResponsibility: 'pendingPatientResponsibility',
};

// ERA detail page - claims table sort options
const ERA_CLAIM_TABLE_SORT_OPTIONS: SortItemType[] = _.map(
	[
		{
			apikey: ERA_CLAIM_TABLE_SORT_COLUMNS.ClaimAge,
			asc: true,
			label: `Claim Age (${SORT_COLUMN_LABELS.oldToNew})`,
		},
		{
			apikey: ERA_CLAIM_TABLE_SORT_COLUMNS.ClaimAmount,
			asc: false,
			label: `Billed (${SORT_COLUMN_LABELS.highToLow})`,
		},
		{
			apikey: ERA_CLAIM_TABLE_SORT_COLUMNS.PatientResponsibility,
			asc: false,
			label: `Patient Responsibility (${SORT_COLUMN_LABELS.highToLow})`,
		},
		{
			apikey: ERA_CLAIM_TABLE_SORT_COLUMNS.PendingPatientResponsibility,
			asc: false,
			label: `Pending PR (${SORT_COLUMN_LABELS.highToLow})`,
		},
	],
	(option) => ({ ...option, key: option.label }),
);

export {
	ERA_FILTER_FIELDS,
	PAYMENT_RECONCILIATION_STATUS,
	PAYMENT_RECONCILIATION_OUTCOME,
	RECONCILED_STATUS_OPTIONS,
	ERA_CLAIM_FILTER_FIELDS,
	ERA_STATE,
	ERA_TABLE_SORT_COLUMNS,
	ERA_TABLE_SORT_OPTIONS,
	ERA_CLAIM_TABLE_SORT_OPTIONS,
	POST_PAYMENT_BATCH_STATUS,
	POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS,
	POST_PAYMENT_GROUPED_STATUS,
};
